import React, { useEffect, useRef, useState } from 'react';

const DISCIPLINE_COLORS = {
  sea_kayaking: '#0057ff',
  whitewater: '#ff3300',
  canoe: '#c47800',
  surfski: '#00998a',
  sup: '#8800ff',
  other: '#2d7a2d',
};

function loadLeaflet() {
  return new Promise((resolve) => {
    if (window.L && window.L.MarkerClusterGroup) { resolve(window.L); return; }

    const loaded = [];

    // Load CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(link);
    }
    if (!document.getElementById('leaflet-cluster-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-cluster-css';
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.Default.min.css';
      document.head.appendChild(link);
    }

    const loadScript = (id, src) => new Promise(res => {
      if (document.getElementById(id)) { res(); return; }
      const s = document.createElement('script');
      s.id = id;
      s.src = src;
      s.onload = res;
      document.head.appendChild(s);
    });

    loadScript('leaflet-js', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js')
      .then(() => loadScript('leaflet-cluster-js', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/leaflet.markercluster.min.js'))
      .then(() => resolve(window.L));
  });
}

function makeIcon(L, discipline) {
  const color = DISCIPLINE_COLORS[discipline] || DISCIPLINE_COLORS.other;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white" opacity="0.9"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [24, 32],
    iconAnchor: [12, 32],
    popupAnchor: [0, -32],
  });
}

export default function MapView({ opportunities, onEventClick, onNoCoords }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const [noCoords, setNoCoords] = useState(0);

  // Load Leaflet once
  useEffect(() => {
    loadLeaflet().then(() => setLeafletLoaded(true));
  }, []);

  // Init map once Leaflet is loaded
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || mapInstanceRef.current) return;
    const L = window.L;
    const map = L.map(mapRef.current, {
      center: [38, -50],
      zoom: 3,
      zoomControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);
    mapInstanceRef.current = map;
  }, [leafletLoaded]);

  // Update markers when opportunities change
  useEffect(() => {
    if (!mapInstanceRef.current || !leafletLoaded) return;
    const L = window.L;
    const map = mapInstanceRef.current;

    // Clear existing cluster group
    if (markersRef.current._clusterGroup) {
      map.removeLayer(markersRef.current._clusterGroup);
    }
    markersRef.current = [];

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 40,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    });
    markersRef.current._clusterGroup = cluster;

    const safeOpps = Array.isArray(opportunities) ? opportunities : [];
    const withCoords = [];
    let missing = 0;
    const outfitCoords = {}; // tracks canonical coords per outfit

    safeOpps.forEach(opp => {
      if (!opp.latitude || !opp.longitude) { missing++; return; }
      // Snap coordinates per outfit — same outfit snaps to same point, different outfits keep their own coords
      const outfitKey = (opp.outfit_name || '').toLowerCase().trim();
      if (!outfitCoords[outfitKey]) {
        outfitCoords[outfitKey] = {
          lat: Math.round(opp.latitude * 1000) / 1000,
          lng: Math.round(opp.longitude * 1000) / 1000,
        };
      }
      const lat = outfitCoords[outfitKey].lat;
      const lng = outfitCoords[outfitKey].lng;
      const icon = makeIcon(L, opp.opportunity_class);
      const marker = L.marker([lat, lng], { icon });
      const popupContent = `
        <div style="font-family:sans-serif;min-width:180px;max-width:260px">
          <div style="font-weight:700;font-size:14px;margin-bottom:4px">${opp.opportunity_name || '—'}</div>
          <div style="font-size:12px;color:#666;margin-bottom:6px">${opp.outfit_name || ''}</div>
          <div style="font-size:12px;margin-bottom:4px">${[opp.address_city, opp.address_state, opp.address_country].filter(Boolean).join(', ') || ''}</div>
          ${opp.event_start_date ? `<div style="font-size:12px;color:#1a5c7a">${opp.event_start_date}</div>` : '<div style="font-size:11px;color:#999;font-style:italic">No specific date</div>'}
          <button onclick="window._mapEventClick(${opp.capture_id})" style="margin-top:8px;padding:5px 10px;background:#1a5c7a;color:white;border:none;border-radius:4px;cursor:pointer;font-size:12px;width:100%">View Details</button>
        </div>
      `;
      marker.bindPopup(popupContent);
      cluster.addLayer(marker);
      markersRef.current.push(marker);
      withCoords.push(opp);
    });

    map.addLayer(cluster);
    setNoCoords(missing);

    // Fit map to markers
    if (withCoords.length > 0) {
      map.fitBounds(cluster.getBounds().pad(0.2));
    }
  }, [opportunities, leafletLoaded]);

  // Global click handler for popup button
  useEffect(() => {
    window._mapEventClick = (captureId) => {
      const safeOpps = Array.isArray(opportunities) ? opportunities : [];
      const opp = safeOpps.find(o => o.capture_id === captureId);
      if (opp) onEventClick(opp);
    };
    return () => { delete window._mapEventClick; };
  }, [opportunities, onEventClick]);

  return (
    <div className="map-container">
      <div ref={mapRef} className="map-canvas" />
      {noCoords > 0 && (
        <div className="map-overlay-bar">
          <button className="map-notice map-notice-btn" onClick={onNoCoords}>
            {noCoords} event{noCoords === 1 ? '' : 's'} not shown — no coordinates · Click to view
          </button>
        </div>
      )}
    </div>
  );
}
